import { Icon } from '@/components/ui/Icon';
import { ItemIcon } from '@/components/ui/ItemIcon';
import type { InventoryRowDTO } from '@/api/endpoints';

/** Shared grid for Bag and Collection. `locked` renders the silhouette. */
export function ItemGrid({ items, empty, showLocked }: {
  items: InventoryRowDTO[];
  empty: string;
  showLocked?: boolean;
}) {
  if (!items.length) return <p className="py-6 text-center text-[11px] text-ink-dim">{empty}</p>;

  return (
    <ul className="grid grid-cols-[repeat(auto-fill,minmax(84px,1fr))] gap-2" role="list">
      {items.map((it) => {
        const locked = showLocked && !it.discovered;
        return (
          <li
            key={it.key}
            /* Locked must mask here too, not just in the visible label below — a
               native title tooltip is still a hover away from the item's real
               name and category, which defeats the '???' the label just drew. */
            title={locked ? 'Undiscovered' : `${it.name} · ${it.category}`}
            className={`relative flex aspect-square flex-col items-center justify-center gap-1
                        overflow-hidden rounded-xl border border-[var(--hairline)] bg-black/30 p-1.5
                        text-center ${locked ? 'opacity-35' : ''}`}
            style={locked ? undefined : { boxShadow: `inset 0 0 0 1px ${it.rarity_color}33` }}
          >
            {it.quantity > 0 && (
              <span className="absolute right-1 top-1 font-pixel text-[7px] text-torch-core">
                ×{it.quantity}
              </span>
            )}
            {/* Every item used to render the same box glyph, so a full bag
                was 50 identical boxes. The atlas gives each its own 3D
                render; a locked slot draws the same `lock` glyph used
                elsewhere (Achievements, Withdraw) rather than a raw emoji. */}
            {locked ? (
              <Icon name="lock" size={26} className="text-ink-dim" />
            ) : (
              <ItemIcon itemKey={it.key} size={38} />
            )}
            <span className="text-[9px] font-semibold leading-tight text-ink">
              {locked ? '???' : it.name}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
